import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./News.css";

import {
  FacebookShareButton,
  TwitterShareButton,
  RedditShareButton,
  PinterestShareButton,
  EmailShareButton,
  FacebookIcon,
  XIcon,
  RedditIcon,
  PinterestIcon,
  EmailIcon,
} from "react-share";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function addTargetBlank(html) {
  // Add target="_blank" and rel="noopener noreferrer" to all <a> tags
  let withTarget = html.replace(
    /<a\s+href="([^"]+)"(?![^>]*target=)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer"'
  );
  // Replace newlines with <br>
  return withTarget.replace(/\n/g, "<br />");
}

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${SERVER_URL}/api/news/`)
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setNews(data);
        setLoading(false);
      });
  }, []);

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <Helmet>
        <title>News & Updates – LingoLab</title>
        <meta
          name="description"
          content="Stay up to date with the latest news and updates from LingoLab, including new features, announcements, and more."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://lingolab.ca/news" />
      </Helmet>
      <div className="news-container">
        <header className="news-header">
          <h1 className="news-page-title">News & Updates</h1>
          <div className="header-divider"></div>
        </header>
        <div className="news-list">
          {news
            .slice() // create a shallow copy to avoid mutating state
            .sort((a, b) => new Date(b.date) - new Date(a.date)) // sort descending by date
            .map((entry) => {
              const newsUrl = `https://lingolab.ca/news#news-${entry.id}`;
              return (
                <div
                  key={entry.id}
                  className="news-card"
                  id={`news-${entry.id}`}
                >
                  <div className="news-date">
                    {new Date(entry.date).toLocaleDateString()}
                  </div>
                  <h2 className="news-title">{entry.title}</h2>
                  <div
                    className="news-text"
                    dangerouslySetInnerHTML={{
                      __html: addTargetBlank(entry.text),
                    }}
                  />
                  <div className="news-social-share">
                    <FacebookShareButton url={newsUrl} quote={entry.title}>
                      <FacebookIcon size={32} round />
                    </FacebookShareButton>
                    <TwitterShareButton url={newsUrl} title={entry.title}>
                      <XIcon size={32} round />
                    </TwitterShareButton>
                    <RedditShareButton url={newsUrl} title={entry.title}>
                      <RedditIcon size={32} round />
                    </RedditShareButton>
                    <PinterestShareButton
                      url={newsUrl}
                      media={"/ll_blue_white.png"}
                      description={entry.title}
                    >
                      <PinterestIcon size={32} round />
                    </PinterestShareButton>
                    <EmailShareButton
                      url={newsUrl}
                      subject={entry.title}
                      body={entry.title}
                    >
                      <EmailIcon size={32} round />
                    </EmailShareButton>
                  </div>
                  <NewsComments newsId={entry.id} comments={entry.comments} />
                </div>
              );
            })}
        </div>{" "}
      </div>
    </>
  );
};

export default News;

// --- Comments and Replies ---
function NewsComments({ newsId, comments }) {
  const [text, setText] = useState("");
  const [parent, setParent] = useState(null);
  const [commentList, setCommentList] = useState(comments || []);
  const [error, setError] = useState(null);

  // Optionally, fetch comments again if you want live updates

  const handleSubmit = (e) => {
    e.preventDefault();
    fetch(`${SERVER_URL}/api/news-comments/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
      },
      body: JSON.stringify({
        news: newsId,
        text,
        parent,
      }),
    })
      .then((res) =>
        res.json().then((data) => {
          if (!res.ok) {
            // Check for missing/invalid token error
            if (
              data.detail &&
              (data.detail.includes(
                "Authentication credentials were not provided."
              ) ||
                data.detail.includes(
                  "Authorization header must contain two space-delimited values"
                ))
            ) {
              throw new Error("You must be signed in to comment.");
            }
            throw new Error(
              data.detail || "Login required or error posting comment."
            );
          }
          return data;
        })
      )
      .then((data) => {
        // Add new comment or reply to the correct place
        if (parent) {
          setCommentList((prev) =>
            prev.map((c) =>
              c.id === parent
                ? {
                    ...c,
                    replies: [...(c.replies || []), data],
                  }
                : c
            )
          );
        } else {
          setCommentList((prev) => [...prev, data]);
        }
        setText("");
        setParent(null);
      })
      .catch((err) => setError(err.message));
  };

  return (
    <div className="news-comments">
      <h3>Comments</h3>
      {commentList.map((c) => (
        <CommentItem key={c.id} comment={c} setParent={setParent} />
      ))}
      <form onSubmit={handleSubmit} className="news-comment-form">
        {error && <div className="news-comment-error">{error}</div>}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={parent ? "Reply..." : "Add a comment..."}
          required
        />
        <button type="submit">{parent ? "Reply" : "Comment"}</button>
        {parent && (
          <button type="button" onClick={() => setParent(null)}>
            Cancel Reply
          </button>
        )}
      </form>
    </div>
  );
}

function CommentItem({ comment, setParent }) {
  return (
    <div className="news-comment-item">
      <div>
        <strong>{comment.user_username}</strong>{" "}
        <span>{comment.created_at}</span>
      </div>
      <div>{comment.text}</div>
      <button onClick={() => setParent(comment.id)}>Reply</button>
      {comment.replies && comment.replies.length > 0 && (
        <div className="news-comment-replies">
          {comment.replies.map((reply) => (
            <CommentItem key={reply.id} comment={reply} setParent={setParent} />
          ))}
        </div>
      )}
    </div>
  );
}
